
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { SparkParams } from './types';
import { defaultParams, buttonParams, linkParams } from './sparkParams';

// Type definitions for our context
type VariantType = 'default' | 'button' | 'link';

interface SparkContextType {
  // Debug panel state
  isDebugPanelVisible: boolean;
  toggleDebugPanel: () => void;
  
  // Parameters management
  currentParams: Record<VariantType, SparkParams>;
  previewParams: Record<VariantType, SparkParams>;
  updateParams: (variant: VariantType, params: Partial<SparkParams>) => void;
  previewParamUpdate: (variant: VariantType, params: Partial<SparkParams>) => void;
  resetParams: (variant: VariantType) => void;
  saveParams: () => void;
  hasUnsavedChanges: boolean;
  
  // Panel position
  panelPosition: { x: number, y: number };
  updatePanelPosition: (position: { x: number, y: number }) => void;
  
  // Active variant selection for the debug panel
  activeVariant: VariantType;
  setActiveVariant: (variant: VariantType) => void;
}

// Initial parameters for all variants
const initialParams: Record<VariantType, SparkParams> = {
  default: { ...defaultParams },
  button: { ...buttonParams },
  link: { ...linkParams },
};

// Default panel position
const defaultPosition = { x: 20, y: 20 };

// Storage keys
const STORAGE_KEY = 'spark-debug-params';
const POSITION_KEY = 'spark-debug-panel-position';

// Create context with default values
const SparkContext = createContext<SparkContextType>({
  isDebugPanelVisible: false,
  toggleDebugPanel: () => {},
  currentParams: initialParams,
  previewParams: initialParams,
  updateParams: () => {},
  previewParamUpdate: () => {},
  resetParams: () => {},
  saveParams: () => {},
  hasUnsavedChanges: false,
  panelPosition: defaultPosition,
  updatePanelPosition: () => {},
  activeVariant: 'default',
  setActiveVariant: () => {},
});

// Hook to easily use the context
export const useSparkContext = () => useContext(SparkContext);

// Provider component to wrap around the application
export const SparkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Debug panel visibility state
  const [isDebugPanelVisible, setIsDebugPanelVisible] = useState(false);
  
  // Parameters state for all variants
  const [params, setParams] = useState<Record<VariantType, SparkParams>>(() => {
    // Try to load saved parameters from localStorage
    const savedParams = localStorage.getItem(STORAGE_KEY);
    return savedParams ? JSON.parse(savedParams) : initialParams;
  });
  
  // Preview parameters state (for real-time updates without saving)
  const [previewParams, setPreviewParams] = useState<Record<VariantType, SparkParams>>(params);
  
  // Track if there are unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Panel position state
  const [panelPosition, setPanelPosition] = useState(() => {
    const savedPosition = localStorage.getItem(POSITION_KEY);
    return savedPosition ? JSON.parse(savedPosition) : defaultPosition;
  });
  
  // Track active variant for the debug panel
  const [activeVariant, setActiveVariant] = useState<VariantType>('button');

  // Toggle debug panel visibility
  const toggleDebugPanel = useCallback(() => {
    setIsDebugPanelVisible(prev => !prev);
  }, []);

  // Update panel position
  const updatePanelPosition = useCallback((position: { x: number, y: number }) => {
    setPanelPosition(position);
    localStorage.setItem(POSITION_KEY, JSON.stringify(position));
  }, []);

  // Preview parameter updates (real-time but not saved)
  const previewParamUpdate = useCallback((variant: VariantType, newParams: Partial<SparkParams>) => {
    setPreviewParams(prevParams => {
      const updated = {
        ...prevParams,
        [variant]: {
          ...prevParams[variant],
          ...newParams,
        },
      };
      setHasUnsavedChanges(JSON.stringify(updated) !== JSON.stringify(params));
      return updated;
    });
  }, [params]);

  // Update parameters for a specific variant (and mark as saved)
  const updateParams = useCallback((variant: VariantType, newParams: Partial<SparkParams>) => {
    setParams(prevParams => ({
      ...prevParams,
      [variant]: {
        ...prevParams[variant],
        ...newParams,
      },
    }));
  }, []);

  // Reset parameters for a specific variant to last saved state (not to defaults)
  const resetParams = useCallback((variant: VariantType) => {
    // Reset to the last saved params instead of initial defaults
    const resetValues = params[variant];
    setPreviewParams(prevParams => ({
      ...prevParams,
      [variant]: resetValues,
    }));
    setHasUnsavedChanges(false);
  }, [params]);

  // Save parameters to localStorage
  const saveParams = useCallback(() => {
    setParams(previewParams);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(previewParams));
    setHasUnsavedChanges(false);
  }, [previewParams]);

  // NOTE: The keyboard shortcut listener has been removed as requested
  // This means Alt+D will no longer toggle the debug panel

  // Synchronize previewParams with params when params change
  useEffect(() => {
    setPreviewParams(params);
  }, [params]);

  // Provide context values to children
  return (
    <SparkContext.Provider
      value={{
        isDebugPanelVisible,
        toggleDebugPanel,
        currentParams: params,
        previewParams,
        updateParams,
        previewParamUpdate,
        resetParams,
        saveParams,
        hasUnsavedChanges,
        panelPosition,
        updatePanelPosition,
        activeVariant,
        setActiveVariant,
      }}
    >
      {children}
    </SparkContext.Provider>
  );
};
